import type { GetCaseRecordResponse } from '@compass/client/types/case_record';
import { caseRecordService } from '@services/api';
import { queryKeys } from '@services/query.config';
import { useQueries } from '@tanstack/react-query';
import { useMemo } from 'react';

import { useStableFold } from '@/hooks/internal/useStableFold';

import { LOOKBACK_DAYS } from '../data/constants';

/**
 * States that should be excluded from the Workforce Console because the work is over.
 * Server-side filter via `FindCaseRecordsRequest.exclude_states`.
 */
const EXCLUDED_STATES = ['completed', 'terminated', 'cancelled'];

/**
 * Soft cap on `(org, program)` fan-out. `FindCaseRecords` still requires
 * `program_id` as a URL path parameter, so we issue one query per program the
 * selected teams serve — deduped across teams (the team dimension collapsed
 * into the `assigned_user_ids` filter). This is always ≤ the old per-(team,
 * program) count. The cap bounds the request rate; a batched/org-wide backend
 * endpoint would be needed to lift it. Past the cap nothing is fetched and
 * `isOverFanout` tells the console to ask for a smaller team selection.
 */
export const MAX_PROGRAM_FANOUT = 40;

/**
 * Server cap on list filters: `assigned_user_ids` with more than 100 ids is
 * rejected (`internal/db/filter.go` MaxFilterListSize). Selecting several large
 * teams can exceed it for one program, so the assignee list is split into
 * batches of this size, one query per batch.
 */
const MAX_ASSIGNEES_PER_QUERY = 100;

/**
 * Split a program's assignee ids into batches the server accepts.
 *
 * Pure function — exported for unit tests.
 */
export function chunkAssigneeIds(ids: string[], size = MAX_ASSIGNEES_PER_QUERY): string[][] {
  const chunks: string[][] = [];
  for (let i = 0; i < ids.length; i += size) chunks.push(ids.slice(i, i + size));
  return chunks;
}

function sevenDaysAgoISO(): string {
  return new Date(Date.now() - LOOKBACK_DAYS * 24 * 60 * 60 * 1000).toISOString();
}

export interface ProgramCasesTuple {
  orgId: string;
  programId: string;
  /** Union of `member_ids + manager_id` of the selected teams serving this
   *  program. Non-empty by construction (see `buildProgramCasesTuples`). */
  assignedUserIds: string[];
}

/**
 * Fetches non-terminated case records for a set of (organization, program)
 * tuples, filtered by ASSIGNEE rather than team. Fans out one query per tuple
 * (one per 100 assignees when a program has more) —
 * `program_id` is a required path param on `FindCaseRecords`, and
 * `assigned_user_ids` narrows to cases owned by the selected teams' members in
 * that program (regardless of which team the case itself belongs to). The
 * program *filter* (FilterBar) is applied client-side downstream, so toggling a
 * program never refetches. Each cache entry can be independently invalidated by
 * a reassign mutation. Supports cross-org fan-out.
 *
 * - `assigned_user_ids` sent comma-joined (backend splits on `,`) for a compact URL
 * - `exclude_states: ['completed','terminated','cancelled']` server-side
 * - `created_at_after: now - 7 days` server-side
 * - `staleTime / refetchInterval: 2 min`
 * - `refetchIntervalInBackground: false` avoids polling in inactive tabs
 * - `refetchOnWindowFocus: true` refreshes as soon as the tab regains focus
 */
export const useProgramCases = (params: { tuples: ProgramCasesTuple[] }) => {
  const { tuples } = params;

  // Defensive dedup keyed by (orgId, programId).
  const dedupedTuples = useMemo(() => {
    const seen = new Set<string>();
    const out: ProgramCasesTuple[] = [];
    for (const t of tuples) {
      const key = `${t.orgId}::${t.programId}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(t);
    }
    return out;
  }, [tuples]);

  const isOverFanout = dedupedTuples.length > MAX_PROGRAM_FANOUT;
  const effectiveTuples = useMemo(() => {
    if (dedupedTuples.length === 0) return [] as ProgramCasesTuple[];
    if (dedupedTuples.length > MAX_PROGRAM_FANOUT) {
      if (process.env.NODE_ENV !== 'production') {
        // No PHI in this warning — only counts.
        console.warn(
          `[workforce] Skipping useProgramCases fan-out of ${dedupedTuples.length} (org×program) ` +
            `tuples — exceeds soft cap of ${MAX_PROGRAM_FANOUT}. ` +
            `A batched/org-wide backend endpoint would be needed to lift this cap.`,
        );
      }
      return [];
    }
    return dedupedTuples;
  }, [dedupedTuples]);

  // One query per (program, assignee batch). Most programs need one batch.
  const requests = useMemo(
    () =>
      effectiveTuples.flatMap(({ orgId, programId, assignedUserIds }) =>
        chunkAssigneeIds(assignedUserIds).map(batch => ({ orgId, programId, batch })),
      ),
    [effectiveTuples],
  );

  const queries = useQueries({
    queries: requests.map(({ orgId, programId, batch }) => {
      // Comma-joined single value → backend `parseAssignedUserIDs` splits on ',',
      // keeping the query string compact vs one repeated param per id.
      const assigneeCsv = batch.join(',');

      return {
        queryKey: queryKeys.workforce.casesByProgram(orgId, programId, assigneeCsv),
        queryFn: () =>
          caseRecordService.findCaseRecords({
            organization_id: orgId,
            program_id: programId,
            // Single CSV string in the array → backend `parseAssignedUserIDs`
            // splits it on ',' (keeps the query string short vs one param per id).
            assigned_user_ids: [assigneeCsv],
            exclude_states: EXCLUDED_STATES,
            created_at_after: sevenDaysAgoISO(),
            // Server caps `FindCaseRecords.limit` at 100 (LA-7313); asking for
            // more is silently clamped. Console shows the first 100 per query.
            limit: 100,
          }),
        staleTime: 2 * 60 * 1000,
        refetchInterval: 2 * 60 * 1000,
        refetchIntervalInBackground: false,
        // Overrides the global default so a tab that wasn't polling in the
        // background refreshes the moment the manager looks at it again.
        refetchOnWindowFocus: true,
        gcTime: 5 * 60 * 1000,
      };
    }),
  });

  // Dedupe by `case_record.id`. The same case could in principle be returned by
  // two program queries only if it changed program mid-flight; defending against
  // overlapping responses keeps the grid from rendering the same card twice.
  const cases: GetCaseRecordResponse[] = useStableFold(
    queries,
    q => q.data?.case_records?.length ?? 0,
    qs => {
      const seen = new Set<string>();
      const out: GetCaseRecordResponse[] = [];
      for (const q of qs) {
        const list = q.data?.case_records;
        if (!list) continue;
        for (const c of list) {
          const id = c.id;
          if (!id || seen.has(id)) continue;
          seen.add(id);
          out.push(c);
        }
      }
      return out;
    },
  );

  return {
    cases,
    isLoading: queries.some(q => q.isLoading),
    isFetching: queries.some(q => q.isFetching),
    isError: queries.some(q => q.isError),
    isOverFanout,
  };
};
